// Base API configuration with RTK Query
import { createApi, fetchBaseQuery, BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import type { RootState } from '@app/store';
import { tokenStorage } from '../storage/tokenStorage';
import { apiLogger } from '@shared/utils/logger';
import { env } from '@shared/config/env';
import { isDevelopment } from '@shared/config/env';

/** User-friendly message from API error (for toast). Avoids depending on react-toastify in this file. */
let showGlobalError: ((message: string) => void) | null = null;
export function setGlobalApiErrorNotifier(fn: ((message: string) => void) | null) {
  showGlobalError = fn;
}
function notifyApiError(error: FetchBaseQueryError) {
  const data = error?.data as { message?: string; error?: string } | undefined;
  const message =
    data?.message ||
    data?.error ||
    (error.status === 403 && 'You do not have permission for this action.') ||
    (error.status === 404 && 'The requested resource was not found.') ||
    (error.status === 500 && 'Something went wrong. Please try again.') ||
    `Request failed (${error.status || 'network'}).`;
  if (showGlobalError) showGlobalError(message);
}

// Track if refresh is in progress
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

/**
 * Refresh access token using refresh token
 */
const refreshAccessToken = async (): Promise<string | null> => {
  // If already refreshing, return the existing promise
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  
  refreshPromise = (async () => {
    try {
      const refreshToken = tokenStorage.getRefreshToken();
      
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      let response: Response;
      try {
        response = await fetch(
          `${env.API_ENDPOINT}/auth/refresh`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ refreshToken }),
            signal: controller.signal,
          }
        );
      } catch (networkError) {
        // Network error / timeout — keep tokens, let caller handle gracefully
        clearTimeout(timeoutId);
        apiLogger.error('Token refresh network error (keeping tokens):', networkError);
        return null;
      }
      clearTimeout(timeoutId);

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          tokenStorage.clearTokens();
          setTimeout(() => { window.location.href = '/login'; }, 100);
        }
        throw new Error(`Token refresh failed: ${response.status}`);
      }

      const result = await response.json();
      
      // Handle backend's nested response format
      const data = result.data || result;
      const { accessToken, refreshToken: newRefreshToken, expiresIn, accessExpiryMillis } = data;
      
      // Support both field names from backend
      const expiryTime = expiresIn || accessExpiryMillis;
      
      if (!accessToken || !expiryTime) {
        throw new Error('Invalid refresh response format');
      }
      
      // Update tokens in storage
      // Backend returns new refresh token for security (token rotation)
      if (newRefreshToken) {
        tokenStorage.setTokens(accessToken, newRefreshToken, expiryTime);
        apiLogger.log('Stored new access and refresh tokens after refresh');
      } else {
        // Fallback: only update access token if no new refresh token
        tokenStorage.setAccessToken(accessToken, expiryTime);
        apiLogger.log('Stored new access token only after refresh');
      }
      
      return accessToken;
    } catch (error) {
      apiLogger.error('Token refresh failed:', error);
      // Tokens are only cleared above on explicit 401/403 from the server.
      // Network errors / timeouts keep tokens intact.
      return null;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

// Custom base query with auth token injection
const baseQuery = fetchBaseQuery({
  baseUrl: env.API_ENDPOINT,
  prepareHeaders: (headers, { getState }) => {
    // Get token from storage (primary) or state (fallback)
    const token = tokenStorage.getAccessToken() || 
                  (getState() as RootState).auth.accessToken;
    
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    
    return headers;
  },
});

// Base query with automatic token refresh
const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  // Get the URL and method from args for logging
  const url = typeof args === 'string' ? args : args.url;
  const method = typeof args === 'string' ? 'GET' : (args.method || 'GET');
  const body = typeof args === 'string' ? undefined : args.body;
  
  // Log request in development
  if (isDevelopment()) {
    apiLogger.group(`🔵 API ${method} ${url}`, () => {
      if (body) {
        apiLogger.log('Body:', body);
      }
      apiLogger.log('Timestamp:', new Date().toISOString());
    });
  }
  
  const startTime = Date.now();
  
  // Skip token refresh for auth endpoints
  const isAuthEndpoint = url.includes('/auth/login') || 
                        url.includes('/auth/register') || 
                        url.includes('/auth/send-otp') ||
                        url.includes('/auth/verify-otp') ||
                        url.includes('/auth/create-user') ||
                        url.includes('/auth/refresh');
  
  // Check if token needs refresh before making the request (60 seconds buffer)
  // Skip for auth endpoints, when no refresh token exists, or when there is no
  // in-memory access token (e.g. right after a page refresh before initializeAuth
  // completes). In the last case let the 401 handler deal with it.
  if (!isAuthEndpoint && tokenStorage.getRefreshToken() && tokenStorage.getAccessToken()) {
    const needsRefresh = tokenStorage.isTokenExpired(60);
    
    if (needsRefresh && !isRefreshing) {
      const newToken = await refreshAccessToken();
      
      if (!newToken) {
        // Refresh failed, return error
        apiLogger.error(`❌ Token refresh failed for ${method} ${url}`);
        return {
          error: {
            status: 401,
            data: { message: 'Token refresh failed' },
          } as FetchBaseQueryError,
        };
      }
    }
  }

  // Make the request
  let result = await baseQuery(args, api, extraOptions);
  
  const duration = Date.now() - startTime;
  
  // Log response in development
  if (isDevelopment()) {
    if (result.error) {
      apiLogger.group(`❌ API ${method} ${url} - Error`, () => {
        apiLogger.error('Status:', result.error?.status);
        apiLogger.error('Error:', result.error);
        apiLogger.log('Duration:', `${duration}ms`);
      });
    } else {
      apiLogger.group(`✅ API ${method} ${url} - Success`, () => {
        apiLogger.log('Data:', result.data);
        apiLogger.log('Duration:', `${duration}ms`);
      });
    }
  }
  
  // Handle 401 Unauthorized - try to refresh token
  if (result.error && result.error.status === 401) {
    // Don't retry for auth endpoints
    if (isAuthEndpoint) {
      return result;
    }

    // Try to refresh the token (only if refresh token exists)
    if (tokenStorage.getRefreshToken()) {
      apiLogger.log(`🔄 Retrying ${method} ${url} after token refresh...`);
      const newToken = await refreshAccessToken();
      
      if (newToken) {
        // Retry the request with new token
        result = await baseQuery(args, api, extraOptions);
        
        if (isDevelopment()) {
          apiLogger.log(`✅ Retry successful for ${method} ${url}`);
        }
      }
      // If refresh failed due to a network error, tokens were kept by
      // refreshAccessToken. The original 401 error is returned to the caller.
    }
  }

  // Global user-visible error feedback for mutations only (skip 401 when we already redirect).
  // Query errors are handled inline by each component to avoid noisy toasts on background fetches.
  if (result.error && result.error.status !== 401 && api.type === 'mutation') {
    notifyApiError(result.error);
  }

  return result;
};

// Create base API with common configuration
export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'Auth',
    'Booking',
    'Driver',
    'Customer',
    'Fleet',
    'Payment',
    'Admin',
    'Role',
    'City',
    'Branch',
    'VehicleType',
    'VehicleBasePrice',
    'VehiclePeakHourPrice',
    'VehiclePriceType',
    'Banner',
    'ServiceGallery',
    'ServiceCategory',
    'ServiceCityStatus',
    'VehicleServiceMapping',
    'GoodsType',
    'CancelReason',
    'AdminAccess',
    'Dashboard',
    'FAQ',
    'FAQCategory',
    'PricingRule',
    'Pincode',
    'Coupon',
    'ControlSetting',
    'RechargeSubscriptionPlan',
    'RechargeTopupPlan',
    'Referral',
    'AuditLog',
    'Order',
  ],
  endpoints: () => ({}), // Endpoints will be injected in feature APIs
});
